import { IAgentWorkflowCommand } from './agent-workflow.interface';
import type { IAgentResearchHistory } from '@domains/research/shared';

export interface IResearchFilter {
	type: 'string' | 'number' | 'boolean' | 'list';
	values?: Array<{ value: string; label: string }>;
	min?: number;
	max?: number;
}
export interface IResearchProduct {
	asin: string;
	marketplace: string;
	product: string;
	title: string;
	brand?: string;
	price?: number;
	bsr?: number;
}
export interface IResearchWorkflowPage {
	describe(): object;
	execute(command: IAgentWorkflowCommand, signal: AbortSignal): Promise<string>;
}
export interface IResearchWorkflowOptions {
	downloadCsv?(csv: string): void;
	getProducts(): IResearchProduct[];
	getFilters(): Record<string, unknown>;
	getFilterDefinitions(): Record<string, IResearchFilter>;
	applyFilters(filters: Record<string, unknown>): void;
	validateFilters(filters: Record<string, unknown>): void;
	getSearchTerm(): string;
	setSearchTerm(term: string): void;
	submit(): void | Promise<void>;
	isBusy(): boolean;
	hasError?(): boolean;
	isCurrent(): boolean;
	getView(): string;
	setView(view: string): void;
	views(): string[];
	compare?(products: IResearchProduct[]): void;
	maxCompare?: number;
	select?(ids: string[]): void;
	favorite?(products: IResearchProduct[], signal: AbortSignal): Promise<string>;
	history?(products: IResearchProduct[], signal: AbortSignal): Promise<IAgentResearchHistory>;
	board?(products: IResearchProduct[]): void;
	loadMore?(): void;
	stop?(): void;
	requiresKeyword?: boolean;
}

export const researchProductId = (product: IResearchProduct): string => `${product.marketplace}:${product.asin}`;

/** Reuses page handlers and validates the entire filter patch before changing the UI. */
export class ResearchWorkflowPage implements IResearchWorkflowPage {
	private selected: string[] = [];
	private historicalData?: IAgentResearchHistory;
	constructor(private options: IResearchWorkflowOptions) {}

	describe(): object {
		const o = this.options;
		const products = o.getProducts();
		return {
			operations: ['search', 'set_filters', 'set_view', 'select', 'export_csv', 'navigate', ...(o.history ? ['read_history'] : []), ...(o.compare ? ['compare'] : []), ...(o.favorite ? ['add_favorites'] : []), ...(o.board ? ['add_to_board'] : []), ...(o.loadMore ? ['load_more'] : []), ...(o.stop ? ['stop_search'] : [])],
			historyDescription: o.history ? 'read_history retrieves stored BSR observations for one to three loaded product IDs using the existing design-detail endpoint. Read historicalData before answering historical questions. Missing or unavailable history is not evidence of past success. This is a product sample, not niche-wide history.' : undefined,
			historicalData: this.historicalData ? { ...this.historicalData, products: this.historicalData.products.filter((entry) => products.some((p) => researchProductId(p) === entry.id)).map((entry) => ({ ...entry, monthly: entry.monthly?.map((month) => ({ ...month })) })) } : undefined,
			filters: o.getFilterDefinitions(), currentFilters: o.getFilters(), searchTerm: o.getSearchTerm(), view: o.getView(), views: o.views(),
			selectedIds: this.selected.filter((id) => products.some((p) => researchProductId(p) === id)),
			maxCompare: o.maxCompare || 0,
			loadedProducts: products.slice(0, 50).map((p) => ({ id: researchProductId(p), asin: p.asin, marketplace: p.marketplace, title: p.title?.slice(0, 100), bsr: p.bsr, price: p.price })),
			loading: o.isBusy(), requestFailed: o.hasError?.() || false
		};
	}

	async execute(c: IAgentWorkflowCommand, signal: AbortSignal): Promise<string> {
		const o = this.options;
		this.check(signal);
		if (c.operation === 'stop_search' && o.stop) { o.stop(); return 'Stopped the live search.'; }
		if (c.operation === 'set_view') {
			if (!c.view || !o.views().includes(c.view)) throw new Error('That view is unavailable on this page.');
			o.setView(c.view); return `Changed view to ${c.view}.`;
		}
		if (o.isBusy()) throw new Error('The page is still loading. Wait for it to finish before changing it.');
		if (c.operation === 'search' || c.operation === 'set_filters') {
			const next = this.parseFilters(c.filters);
			o.validateFilters({ ...o.getFilters(), ...next });
			const term = c.searchTerm === null ? o.getSearchTerm() : c.searchTerm.trim();
			if (c.operation === 'search' && o.requiresKeyword && !term) throw new Error('Real Time Research requires a keyword.');
			if ('enablePriceFilter' in o.getFilters() && ('from_price' in next || 'to_price' in next) && !('enablePriceFilter' in next)) next['enablePriceFilter'] = true;
			if ('enableBsrFilter' in o.getFilters() && ('from_bsr' in next || 'to_bsr' in next) && !('enableBsrFilter' in next)) next['enableBsrFilter'] = true;
			o.applyFilters(next);
			if (c.searchTerm !== null) o.setSearchTerm(term);
			if (c.operation === 'set_filters') return 'Updated the requested filters. Results have not been refreshed.';
			this.selected = []; o.select?.([]);
			this.historicalData = undefined;
			await o.submit();
			await this.waitForResults(signal);
			return `Search submitted. ${o.getProducts().length} products are currently loaded${o.isBusy() ? '; more results are still arriving' : ''}.`;
		}

		if (c.operation === 'load_more' && o.loadMore) {
			const previousCount = o.getProducts().length;
			o.loadMore(); await this.waitForResults(signal);
			return `Loaded ${Math.max(0, o.getProducts().length - previousCount)} additional products; ${o.getProducts().length} total loaded${o.isBusy() ? "; more results are still arriving" : ""}.`;
		}
		const ids = [...new Set(c.operation === 'select' ? c.ids : c.ids.length ? c.ids : this.selected)];
		const products = ids.map((id) => {
			const product = o.getProducts().find((p) => researchProductId(p) === id);
			if (!product) throw new Error('A selected product is no longer loaded. Select from the current results.');
			return product;
		});
		if (c.operation === 'select') { this.selected = ids; o.select?.(ids); return `Selected ${ids.length} products in FLAI.`; }
		if (!products.length) throw new Error('Select specific loaded products first.');
		if (c.operation === 'read_history' && o.history) {
			if (products.length > 3) throw new Error('Choose up to three loaded products for historical research.');
			this.historicalData = undefined;
			const history = await o.history(products, signal);
			this.check(signal);
			this.historicalData = history;
			const available = history.products.filter((p) => p.status === 'available').length;
			return `Historical lookup finished: stored BSR observations available for ${available} of ${products.length} requested products. Inspect coverage and unavailable records in the snapshot; this does not establish niche-wide sales performance.`;
		}
		if (c.operation === 'compare' && o.compare) {
			if (products.length > (o.maxCompare || 0)) throw new Error(`This page supports comparison of up to ${o.maxCompare} products. No comparison was changed.`);
			o.compare(products); this.selected = ids; return `Opened comparison for ${products.length} products.`;
		}
		if (c.operation === 'add_favorites' && o.favorite) return o.favorite(products, signal);
		if (c.operation === 'add_to_board' && o.board) { o.board(products); return `Added ${products.length} ASINs to the ASIN Board.`; }
		if (c.operation === 'export_csv') {
			const columns = ['asin', 'marketplace', 'product', 'title', 'brand', 'price', 'bsr'] as const;
			// Spreadsheet formula injection must not be introduced by product titles/brands.
			const cell = (value: unknown): string => { const text = String(value ?? ''); return `"${(/^[=+@\-\t\r]/.test(text) ? "'" : '') + text.replace(/"/g, '""')}"`; };
			const csv = [columns.join(','), ...products.map((p) => columns.map((key) => cell(p[key])).join(','))].join('\r\n');
			if (!o.downloadCsv) throw new Error('CSV download is unavailable on this page.');
			o.downloadCsv(csv);
			return `Downloaded a CSV containing ${products.length} selected products.`;
		}
		throw new Error('This action is unavailable on this page.');
	}

	private parseFilters(patch: Array<{ name: string; value: string }>): Record<string, unknown> {
		const definitions = this.options.getFilterDefinitions();
		const result: Record<string, unknown> = {};
		for (const item of patch) {
			const definition = definitions[item.name];
			if (!Object.prototype.hasOwnProperty.call(definitions, item.name) || !definition) throw new Error(`Filter ${item.name} is unavailable.`);
			let value: unknown = item.value;
			if (definition.values && !definition.values.some((v) => v.value === item.value)) throw new Error(`Unsupported value for ${item.name}.`);
			if (definition.type === 'number') {
				value = Number(item.value);
				if (!item.value.trim() || !Number.isFinite(value) || definition.min !== undefined && Number(value) < definition.min || definition.max !== undefined && Number(value) > definition.max) throw new Error(`Invalid number for ${item.name}.`);
			}
			if (definition.type === 'boolean') { if (!['true', 'false'].includes(item.value)) throw new Error(`Invalid boolean for ${item.name}.`); value = item.value === 'true'; }
			if (definition.type === 'list') { value = JSON.parse(item.value); if (!Array.isArray(value) || value.length > 5 || value.some((v) => typeof v !== 'string' || v.length > 80)) throw new Error(`Invalid list for ${item.name}.`); }
			result[item.name] = value;
		}
		return result;
	}

	private check(signal: AbortSignal): void {
		if (signal.aborted || !this.options.isCurrent()) throw new Error('Workflow stopped because the page changed or the user cancelled.');
	}
	private async waitForResults(signal: AbortSignal): Promise<void> {
		// Allow Angular to apply input changes and start the page's existing request flow.
		await new Promise((resolve) => setTimeout(resolve, 100));
		for (let elapsed = 0; elapsed < 120000; elapsed += 250) {
			this.check(signal);
			if (this.options.hasError?.()) throw new Error('The research request failed. Loaded results may be incomplete; this is not an empty search result.');
			if (!this.options.isBusy()) return;
			await new Promise((resolve) => setTimeout(resolve, 250));
		}
		this.check(signal);
	}
}
